/**
 * Подготовка изображений перед загрузкой.
 *
 * Снимок с телефона легко весит 10 МБ при 8000 × 6000 пикселей. Хранить и
 * раздавать такое каждому игроку незачем: на экране кадр всё равно занимает
 * от силы половину окна, а грузиться будет секунды. Поэтому всё, что больше
 * Full HD, ужимается до него с сохранением пропорций — прямо в браузере,
 * до отправки.
 *
 * Сжимать на сервере нельзя без того, чтобы протащить через него байты: файл
 * уходит в хранилище напрямую по presigned-ссылке, и backend их не видит.
 */

/** Габарит, в который вписывается любое загружаемое изображение. */
export const MAX_WIDTH = 1920;
export const MAX_HEIGHT = 1080;

/** Качество перекодирования: выше 0.85 разница уже не видна, а вес растёт. */
const QUALITY = 0.85;

/**
 * Вписывает изображение в заданный габарит, сохраняя пропорции.
 *
 * Файл, который и так помещается, возвращается нетронутым: перекодировать
 * его значило бы потерять качество без всякой выгоды.
 */
export async function fitWithin(
  file: File,
  maxWidth = MAX_WIDTH,
  maxHeight = MAX_HEIGHT,
): Promise<File> {
  const bitmap = await decode(file);
  if (!bitmap) return file;

  try {
    const scale = Math.min(maxWidth / bitmap.width, maxHeight / bitmap.height, 1);
    if (scale === 1) return file;

    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const blob = await draw(bitmap, width, height);
    if (!blob) return file;

    return new File([blob], renamed(file.name, blob.type), { type: blob.type });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Вырезает квадрат и приводит его к стороне size.
 *
 * Квадрат задаётся в координатах исходного изображения — так его выбирает
 * человек в окне кадрирования.
 */
export async function cropSquare(
  file: File,
  crop: { x: number; y: number; size: number },
  size = 512,
): Promise<File> {
  const bitmap = await decode(file);
  if (!bitmap) return file;

  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext("2d");
    if (!context) return file;

    context.imageSmoothingQuality = "high";
    context.drawImage(bitmap, crop.x, crop.y, crop.size, crop.size, 0, 0, size, size);

    const blob = await encode(canvas);
    if (!blob) return file;

    return new File([blob], renamed(file.name, blob.type), { type: blob.type });
  } finally {
    bitmap.close?.();
  }
}

/**
 * Декодирует файл с учётом поворота из EXIF.
 *
 * Без этого снимок с телефона, повёрнутый камерой, после перерисовки
 * в canvas ложится набок: тег ориентации теряется вместе с метаданными.
 */
async function decode(file: File): Promise<ImageBitmap | null> {
  if (!file.type.startsWith("image/")) return null;

  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Формат, который браузер не декодирует, отправляем как есть: пусть
    // решает сервер, а не молча ломается загрузка.
    return null;
  }
}

async function draw(bitmap: ImageBitmap, width: number, height: number): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) return null;

  context.imageSmoothingQuality = "high";
  context.drawImage(bitmap, 0, 0, width, height);
  return encode(canvas);
}

/**
 * Кодирует холст. WebP заметно легче JPEG при том же качестве, но старые
 * браузеры его не кодируют и молча возвращают PNG — тогда берём JPEG.
 */
function encode(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (webp) => {
        if (webp?.type === "image/webp") {
          resolve(webp);
          return;
        }
        canvas.toBlob((jpeg) => resolve(jpeg), "image/jpeg", QUALITY);
      },
      "image/webp",
      QUALITY,
    );
  });
}

/** Меняет расширение под новый тип: имя файла уходит в ключ объекта. */
function renamed(name: string, type: string): string {
  const extension = type === "image/webp" ? "webp" : "jpg";
  return name.replace(/\.[^.]+$/, "") + "." + extension;
}
